import { trackAnalyticsEvent } from 'util/analytics';
import React, { ReactElement, useMemo } from 'react';
import { useRouter } from 'next/router';
import useSWR from 'swr';
import { Box } from '@mui/material';
import { useSchoolsApi } from 'services/api';
import { ListEventsResponse } from 'services/api/apis/events/dto';
import { OrganizationSummary } from 'services/api/models';
import { SectionHeader, EmptyCard, useOrgModal, localNowIso } from '../shared';
import { PickedEventCard, PickedEventCardSkeleton } from './PickedEventCard';

export interface PickedForYouProps {
  schoolSlug: string | null;

  isDesktop: boolean;
}

const CARD_WIDTH = 280;

// Desktop: fixed-width cards that fit as many per row as the space allows, then wrap.
const gridSx = {
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fill, minmax(min(100%, ${CARD_WIDTH}px), ${CARD_WIDTH}px))`,
  gap: '32px',
  width: '100%',
} as const;

// Mobile (per Figma node 6373:5528): a single horizontal row of 320px cards that snap, with the
// next card peeking. The negative inline margin + matching padding bleeds the scroll area to the
// screen edges while keeping the first card inset by the page's 20px gutter.
const carouselSx = {
  display: 'flex',
  gap: '12px',
  overflowX: 'auto',
  scrollSnapType: 'x mandatory',
  WebkitOverflowScrolling: 'touch',
  mx: '-20px',
  px: '20px',
  scrollPaddingLeft: '20px',
  pb: '4px',
  '&::-webkit-scrollbar': { display: 'none' },
  scrollbarWidth: 'none',
} as const;

const carouselItemSx = {
  scrollSnapAlign: 'start',
  flexShrink: 0,
  width: `${CARD_WIDTH}px`,
} as const;

export const PickedForYou = ({
  schoolSlug,
  isDesktop,
}: PickedForYouProps): ReactElement => {
  const router = useRouter();
  const schoolsApi = useSchoolsApi();
  const { openOrg, orgModal } = useOrgModal();

  const now = useMemo(() => localNowIso(), []);

  const { data, isValidating } = useSWR<ListEventsResponse>(
    schoolSlug ? ['for-you/picked', schoolSlug, now] : null,
    async () => {
      const res = await schoolsApi.getSchoolEvents(schoolSlug as string, {
        timeFilter: 'upcoming',
        now,
        windowDays: 7,
        pageSize: 8,
      });
      return res.success ? res.value : { count: 0, events: [], cursor: null };
    },
  );

  const events = data?.events ?? [];
  const isLoading = !data && isValidating;

  const goToEvent = (url: string): void => {
    trackAnalyticsEvent({ name: 'recommended_events_row_clicked' });
    void router.push(url);
  };

  const renderCard = (event: ListEventsResponse['events'][number]): ReactElement => (
    <PickedEventCard
      key={event.id}
      event={event}
      onDetails={() => goToEvent(event.url)}
      onEventClick={() => goToEvent(event.url)}
      onOrgClick={(org: OrganizationSummary) => openOrg(org)}
    />
  );

  const renderBody = (): ReactElement => {
    if (isLoading) {
      return isDesktop ? (
        <Box sx={gridSx}>
          {[0, 1, 2, 3].map(i => (
            <PickedEventCardSkeleton key={i} />
          ))}
        </Box>
      ) : (
        <Box sx={carouselSx}>
          {[0, 1].map(i => (
            <Box key={i} sx={carouselItemSx}>
              <PickedEventCardSkeleton />
            </Box>
          ))}
        </Box>
      );
    }

    if (events.length === 0) {
      return (
        <EmptyCard
          icon="event_note"
          title="Quiet week on campus."
          body="Nothing big on the schedule yet. Things will usually pick up — peek back then."
        />
      );
    }

    return isDesktop ? (
      <Box sx={gridSx}>{events.map(renderCard)}</Box>
    ) : (
      <Box sx={carouselSx}>
        {events.map(event => (
          <Box key={event.id} sx={carouselItemSx}>
            {renderCard(event)}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box width="100%">
      <SectionHeader title="Events happening soon" titleSize={24} />
      <Box mt="16px">{renderBody()}</Box>
      {orgModal}
    </Box>
  );
};
